import { z } from "zod";
import {
    EMAIL_FORMAT_REGEX,
    HARDCODED_REQUEST_TYPE,
    HARDCODED_SENDER,
    SAMPLE_DEFAULTS,
    TOAST_MESSAGES,
    VALIDATION_ERROR_MESSAGES,
    VARIABLE_NAME_TO_SAMPLE_VALUE,
} from "./constants";

/**
 * Zod validation schema for the Test Email Template form.
 * Field-level error messages come from {@link VALIDATION_ERROR_MESSAGES}.
 */
export const TEST_EMAIL_TEMPLATE_FORM_SCHEMA = z.object({
    template: z.string().min(1, VALIDATION_ERROR_MESSAGES.TEMPLATE_REQUIRED),
    toEmail: z
        .string()
        .min(1, VALIDATION_ERROR_MESSAGES.TO_EMAIL_REQUIRED)
        .regex(EMAIL_FORMAT_REGEX, VALIDATION_ERROR_MESSAGES.TO_EMAIL_INVALID),
    recipientId: z.string().optional(),
    subjects: z.string().min(1, VALIDATION_ERROR_MESSAGES.SUBJECTS_REQUIRED),
    message: z.string().min(1, VALIDATION_ERROR_MESSAGES.MESSAGE_REQUIRED),
    variables: z.record(z.string(), z.string().or(z.boolean())),
});

/**
 * Builds the initial form `variables` values from the template's typed map
 * (`name → "string" | "boolean"`). Entries typed `"boolean"` start as `true`
 * (test form should show the paragraph). Every other name is pre-filled from
 * {@link VARIABLE_NAME_TO_SAMPLE_VALUE}, falling back to
 * {@link SAMPLE_DEFAULTS.FALLBACK_VARIABLE_VALUE}.
 *
 * @param {Record<string, string>} currentTemplateVariables Name → type map from the BFF.
 * @returns {Record<string, string | boolean>} Variable name → sample value map.
 */
export function getInitialVariableValues(currentTemplateVariables) {
    const result = {};

    for (const [variableName, type] of Object.entries(currentTemplateVariables)) {
        if(type === "boolean") {
            result[variableName] = true;
        }
        else{
            result[variableName] =
            VARIABLE_NAME_TO_SAMPLE_VALUE[variableName] ??
            SAMPLE_DEFAULTS.FALLBACK_VARIABLE_VALUE;
        }
    }
    return result;
}

/**
 * Builds the complete initial (or reset) form values for the Test Email Template form.
 * `toEmail` is always empty; all other fields receive deterministic sample defaults.
 *
 * @param {{ firstTemplate: string, currentTemplateVariables: Record<string, string>, currentTemplateSubject?: string }} params
 * @returns {import("react-hook-form").DefaultValues<import("zod").infer<typeof TEST_EMAIL_TEMPLATE_FORM_SCHEMA>>}
 */
export function getInitialFormValues({
  firstTemplate,
  currentTemplateVariables,
  currentTemplateSubject,
}) {
  return {
    template: firstTemplate,
    toEmail: "",
    recipientId: "",
    subjects: currentTemplateSubject ?? SAMPLE_DEFAULTS.FALLBACK_SUBJECT,
    message: SAMPLE_DEFAULTS.MESSAGE,
    variables: getInitialVariableValues(
      currentTemplateVariables,
    ),
  };
}

/**
 * Filters form `variables` down to the keys in the typed map. Extra keys are dropped.
 * `"string"` entries: missing values become `""`.
 * `"boolean"` entries: copied when the value is a real boolean; omitted otherwise
 * (never `""` / `"false"` — Mustache treats a non-empty string as shown).
 *
 * @param {Record<string, string | boolean>} formVariables The raw `variables` values from the form state.
 * @param {Record<string, string>} currentTemplateVariables Name → type map from the BFF.
 * @returns {Record<string, string | boolean>}
 */
export function filterVariablesToTemplateVariables(formVariables, currentTemplateVariables) {
    const result = {};

    for (const [variableName, type] of Object.entries(currentTemplateVariables)) {
        const value = formVariables[variableName];

        if (type === "boolean") {
            if (typeof value === "boolean"){
                result[variableName] = value;
            }
        }
        else{
            result[variableName] = value ?? "";
        }
    }
    return result;
}

/**
 * Builds the POST request body for the `sendDirectEmail` endpoint.
 * Omits `recipientId` when empty (backend environment default kicks in).
 * Hardcodes `senderId`, `senderEmail`, and `type` from client-side constants.
 * Never sends `attachments`, `conversationId`, `sendType`, or `recipientType`.
 * Entries typed `"boolean"` in the map are forwarded as JSON booleans (or omitted); see
 * {@link filterVariablesToTemplateVariables}.
 *
 * @param {import("zod").infer<typeof TEST_EMAIL_TEMPLATE_FORM_SCHEMA>} formValues
 * @param {Record<string, string>} currentTemplateVariables Name → type map from the BFF.
 * @returns {object} Request body matching the sendDirectEmail contract.
 */
export function buildSendDirectEmailRequestBody(formValues, currentTemplateVariables) {
    const body = {
        toEmail: formValues.toEmail,
        senderEmail: HARDCODED_SENDER.EMAIL,
        senderId: HARDCODED_SENDER.ID,
        template: formValues.template,
        subjects: formValues.subjects,
        message: formValues.message,
        variables: filterVariablesToTemplateVariables(
            formValues.variables,
            currentTemplateVariables,
        ),
        type: HARDCODED_REQUEST_TYPE,
    };

    const recipientIdIsPresent = formValues.recipientId && formValues.recipientId.trim() !== "";
    if (recipientIdIsPresent) {
        body.recipientId = formValues.recipientId.trim();
    }

    return body;
}

/**
 * Extracts a user-facing error message from a caught error.
 * Prefers the backend-supplied `message` from the error cause, then the error's own message,
 * then falls back to the generic failure string.
 *
 * @param {unknown} error The error caught from a failed mutation or fetch.
 * @returns {string}
 */
export function extractBackendErrorMessage(error) {
    if (error?.cause?.message) {
        return error.cause.message;
    }
    if (error?.message) {
        return error.message;
    }
    return TOAST_MESSAGES.FALLBACK_ERROR;
}
